import React from "react";
import Section from "./Section";
import AnimatedCounter from "./AnimatedCounter";
import { motion } from "framer-motion";

const kpis = [
  {
    value: 100,
    suffix: "%",
    label: "Task completion",
    sub: "Across 2,609 processed requests",
    id: "task-completion",
  },
  {
    value: 89.8,
    decimals: 1,
    suffix: "%",
    label: "SLA compliance",
    sub: "97.9% in Housekeeping",
    id: "sla-compliance",
  },
  {
    value: 91,
    suffix: "+",
    label: "Service types managed",
    sub: "In a single connected platform",
    id: "service-types",
  },
  {
    value: 22,
    suffix: "%",
    label: "Platform growth",
    sub: "May → July 2025 adoption",
    id: "platform-growth",
  },
];

const projections = [
  { v: "12–20×", l: "Projected ROI multiplier" },
  { v: "US$ 45K–70K", l: "Projected annual savings" },
  { v: "US$ 11,708", l: "Realised annual savings" },
  { v: "0", l: "System downtime · 92 days" },
];

export default function CustomerSuccess() {
  return (
    <Section
      id="customer-success"
      overline="Customer success"
      testId="customer-success-section"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-8">
          <h2
            className="font-display text-white text-[36px] md:text-[56px] leading-[1.02] tracking-tight text-balance"
            style={{ fontWeight: 800 }}
          >
            <span className="grad-text">The Eclipse Hotel.</span>{" "}
            2,609 requests. 91 service types. Zero downtime.
          </h2>
        </div>
        <div className="md:col-span-4">
          <p className="text-white/75 text-[15px] leading-relaxed">
            A 92-day case study of a single property running iNPLASS across
            every department. Numbers that speak for themselves.
          </p>
        </div>
      </div>

      {/* KPI grid */}
      <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => (
          <motion.div
            key={k.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.08 }}
            className="relative rounded-xl brand-panel p-7 overflow-hidden group hover:border-brand-purpleLight/60 transition-colors"
            data-testid={`kpi-${k.id}`}
          >
            <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-brand-orange/20 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
            <div
              className="font-display text-white text-[44px] md:text-[56px] leading-none tracking-tight"
              style={{ fontWeight: 800 }}
            >
              <AnimatedCounter
                value={k.value}
                suffix={k.suffix}
                decimals={k.decimals || 0}
                testId={`kpi-value-${k.id}`}
              />
            </div>
            <div className="mt-3 font-display text-[11px] uppercase tracking-[0.28em] text-white/80 font-bold">
              {k.label}
            </div>
            <div className="mt-1 text-white/65 text-[13px]">{k.sub}</div>
          </motion.div>
        ))}
      </div>

      {/* Projections strip */}
      <div className="mt-6 rounded-2xl relative overflow-hidden">
        <div className="absolute inset-0 bg-brand-diagonal opacity-95" />
        <div className="absolute inset-0 bg-black/25" />
        <div className="relative p-6 md:p-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {projections.map((p) => (
            <div
              key={p.l}
              data-testid={`projection-${p.l.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div
                className="font-display text-white text-[26px] md:text-[32px] leading-tight"
                style={{ fontWeight: 800 }}
              >
                {p.v}
              </div>
              <div className="mt-1.5 font-display text-[10px] uppercase tracking-[0.28em] text-white/80 font-bold">
                {p.l}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}
